package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.ReminderFilter
import com.dotdrives.model.ReminderSummary
import com.dotdrives.repository.ReminderRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlin.time.Instant

data class NextStepsUiState(
    val loading: Boolean = false,
    val reminders: List<ReminderSummary> = emptyList(),
    val filter: ReminderFilter = ReminderFilter.All,
    val error: String? = null,
)

class NextStepsController(
    private val repo: ReminderRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val currentUserId: String,
) : DotController<NextStepsUiState>(NextStepsUiState(), scope) {
    private var generation = 0L

    fun load() {
        val current = nextGeneration()
        val filter = state.value.filter
        update { it.copy(loading = true, error = null) }
        scope.launch {
            try {
                repo.list(orgId, filter, currentUserId)
                    .onSuccess { reminders ->
                        if (!isCurrent(current)) return@onSuccess
                        update { it.copy(loading = false, reminders = reminders, error = null) }
                    }
                    .onFailure { publishError(it, current) }
            } catch (error: Exception) {
                publishError(error, current)
            }
        }
    }

    fun setFilter(filter: ReminderFilter) {
        update { it.copy(filter = filter, error = null) }
        load()
    }

    fun retry() = load()

    fun complete(reminderId: String) {
        val previous = state.value.reminders
        if (previous.none { it.id == reminderId }) return
        update { it.copy(reminders = it.reminders.filterNot { r -> r.id == reminderId }, error = null) }
        scope.launch {
            repo.complete(orgId, reminderId).onFailure { error ->
                if (error is CancellationException) throw error
                update { it.copy(reminders = previous, error = error.message ?: "Something went wrong") }
            }
        }
    }

    fun snooze(reminderId: String, dueAt: Instant) {
        val previous = state.value.reminders
        val existing = previous.firstOrNull { it.id == reminderId } ?: return
        update {
            it.copy(
                reminders = it.reminders.map { r -> if (r.id == reminderId) r.copy(dueAt = dueAt) else r },
                error = null,
            )
        }
        scope.launch {
            repo.snooze(orgId, reminderId, dueAt).onFailure { error ->
                if (error is CancellationException) throw error
                update {
                    it.copy(
                        reminders = it.reminders.map { r -> if (r.id == reminderId) existing else r },
                        error = error.message ?: "Something went wrong",
                    )
                }
            }
        }
    }

    fun reassign(reminderId: String, userId: String, userName: String?) {
        val previous = state.value.reminders
        val existing = previous.firstOrNull { it.id == reminderId } ?: return
        update {
            it.copy(
                reminders = it.reminders.map { r ->
                    if (r.id == reminderId) r.copy(assignedToUserId = userId, assignedToName = userName) else r
                },
                error = null,
            )
        }
        scope.launch {
            repo.reassign(orgId, reminderId, userId).onFailure { error ->
                if (error is CancellationException) throw error
                update {
                    it.copy(
                        reminders = it.reminders.map { r -> if (r.id == reminderId) existing else r },
                        error = error.message ?: "Something went wrong",
                    )
                }
            }
        }
    }

    private fun publishError(error: Throwable, generation: Long) {
        if (error is CancellationException) throw error
        if (!isCurrent(generation)) return
        // A transient load failure must surface as error+retry without wiping the last-known
        // list back to empty -- that combination reads as "you have nothing configured."
        update { it.copy(loading = false, error = error.message ?: "Something went wrong") }
    }

    private fun nextGeneration(): Long {
        generation += 1
        return generation
    }

    private fun isCurrent(candidate: Long): Boolean = candidate == generation
}
